// SensorsHelper.ts - 神策数据集成辅助工具

declare global {
    interface Window {
        sensorsDataAnalytic201505?: any;
        sensors?: any;
    }
}

/**
 * 神策数据辅助类
 * 提供统一的用户登录跟踪功能
 */
export default class SensorsHelper {
    /**
     * 检查神策SDK是否已加载
     */
    static isSensorsLoaded(): boolean {
        return !!(window.sensorsDataAnalytic201505 || window.sensors);
    }

    /**
     * 获取神策SDK实例
     */
    static getSensorsInstance(): any {
        return window.sensorsDataAnalytic201505 || window.sensors;
    }

    /**
     * 用户登录时调用
     * @param userId 用户ID
     */
    static trackUserLogin(userId: string | number): void {
        if (!this.isSensorsLoaded()) {
            console.warn('[SensorsHelper] Sensors SDK not loaded, skipping login tracking');
            return;
        }

        try {
            const sensors = this.getSensorsInstance();
            if (sensors && typeof sensors.login === 'function') {
                // 确保传入的是字符串类型的用户ID
                const userIdStr = String(userId);
                sensors.login(userIdStr);
                console.log('[SensorsHelper] User login tracked for ID:', userIdStr);
            } else {
                console.warn('[SensorsHelper] sensors.login method not available');
            }
        } catch (error) {
            console.error('[SensorsHelper] Error tracking user login:', error);
        }
    }

    /**
     * 从浏览器执行环境中调用用户登录跟踪
     * 这个方法可以在PHP代码注入的JavaScript中使用
     * @param userId 用户ID
     */
    static trackUserLoginFromPHP(userId: string | number): void {
        // 等待DOM加载完成
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.trackUserLogin(userId);
            });
        } else {
            this.trackUserLogin(userId);
        }
    }

    /**
     * 为已登录用户执行登录跟踪
     * 通常在页面加载时调用，确保已登录用户的身份正确关联
     * @param userId 用户ID
     */
    static trackExistingUserLogin(userId: string | number): void {
        // 延迟执行，确保神策SDK已完全初始化
        setTimeout(() => {
            this.trackUserLogin(userId);
        }, 100);
    }

    /**
     * 用户登出时调用
     * 清除当前用户的登录状态，使后续事件不再关联到之前的用户标识
     */
    static trackUserLogout(): void {
        if (!this.isSensorsLoaded()) {
            console.warn('[SensorsHelper] Sensors SDK not loaded, skipping logout tracking');
            return;
        }

        try {
            const sensors = this.getSensorsInstance();
            if (sensors && typeof sensors.logout === 'function') {
                sensors.logout();
                console.log('[SensorsHelper] User logout tracked');
            } else {
                console.warn('[SensorsHelper] sensors.logout method not available');
            }
        } catch (error) {
            console.error('[SensorsHelper] Error tracking user logout:', error);
        }
    }

    /**
     * 从浏览器执行环境中调用用户登出跟踪
     * 这个方法可以在PHP代码注入的JavaScript中使用
     */
    static trackUserLogoutFromPHP(): void {
        // 等待DOM加载完成
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                this.trackUserLogout();
            });
        } else {
            this.trackUserLogout();
        }
    }
}

// 将SensorsHelper暴露到全局作用域，以便PHP注入的代码可以使用
if (typeof window !== 'undefined') {
    (window as any).SensorsHelper = SensorsHelper;
}
